import { LoaderCircleIcon, SparklesIcon } from "lucide-react";
import { Button } from "../ui/button";
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
} from "../ui/sheet";
import { ProjectLeadSheetSections } from "./ProjectLeadSheetSections";
import { ProjectLeadWelcome } from "./ProjectLeadWelcome";
import { useProjectLeadSheet } from "./useProjectLeadSheet";

const SHEET_CLASS =
  "h-dvh w-full max-w-none overflow-hidden rounded-none border-0 bg-card p-0 text-card-foreground shadow-2xl shadow-black/35 sm:h-full sm:w-[min(92vw,520px)] sm:rounded-xl sm:border sm:border-border";

export interface ProjectLeadSheetProps {
  readonly projectId: string;
  readonly projectTitle: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onPlanWork: () => void;
}

export function ProjectLeadSheet({
  projectId,
  projectTitle,
  open,
  onClose,
  onPlanWork,
}: ProjectLeadSheetProps) {
  const sheet = useProjectLeadSheet(projectId, open);
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetPopup side="right" variant="inset" showCloseButton className={SHEET_CLASS}>
        <SheetHeader className="border-b border-border pr-14">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <SparklesIcon className="size-4" aria-hidden /> Project Lead
          </div>
          <SheetTitle>{projectTitle}</SheetTitle>
          <SheetDescription>
            Your project assistant for plans, decisions, and project context.
          </SheetDescription>
        </SheetHeader>
        <SheetPanel className="space-y-6">
          {sheet.loading && sheet.data === null ? (
            <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
              <LoaderCircleIcon className="size-4 animate-spin" aria-hidden /> Loading Project Lead…
            </div>
          ) : null}
          {sheet.error ? (
            <div className="rounded-lg border border-destructive-border bg-destructive-surface p-4 text-sm text-destructive">
              <p>{sheet.error}</p>
              <Button className="mt-3" size="sm" variant="outline" onClick={sheet.reload}>
                Retry
              </Button>
            </div>
          ) : null}
          {sheet.actionError ? (
            <p className="rounded-lg border border-destructive-border bg-destructive-surface p-3 text-xs text-destructive">
              {sheet.actionError}
            </p>
          ) : null}
          {sheet.data ? (
            <>
              <ProjectLeadWelcome projectTitle={projectTitle} onPlanWork={onPlanWork} />
              <ProjectLeadSheetSections data={sheet.data} onDecideApproval={sheet.decideApproval} />
            </>
          ) : null}
        </SheetPanel>
      </SheetPopup>
    </Sheet>
  );
}
